import React, {
    useCallback,
    useMemo,
    useState,
    useEffect,
    useRef,
    useContext,
} from "react";
import classes from "./AssistantMessage.module.css";
import {Button, Group, Text, Tooltip} from "@mantine/core";
import IconCopy from "../../../assets/copy.svg?react";
import IconRefresh from "../../../assets/refresh.svg?react";
import IconCheck from "../../../assets/check.svg?react";
import IconKAI from "../../../assets/kai-chat-logo.svg?react";
import {Rater} from "../components/rating/Rater";
import {
    llmBadJsonToGoodJson,
    prettyMessageForClipboard,
    plainTextForClipboard,
} from "../utils";
import {
    AssistantAnswer,
    AssistantMessage as AssistantMessageType,
    KAIResponseType,
    MessageWithMetadata,
} from "../../../types";
import {useAppSelector} from "../../../app/hooks";
import {useNavigate} from "react-router-dom";
import {clearThread} from "../chatSlice";
import {useDispatch} from "react-redux";
import {ReferencesBlock} from "../components/stream-parser/blocks/ReferencesBlock";
import {GPTStreamParser} from "../components/stream-parser/GPTStreamParser";
import {ErrorReason} from "../components/error-handling/ErrorReason";
import {useTranslation} from "react-i18next";
import {AssistantWorkBox} from "./AssistantWorkBox";
import {SuggestKaiPlus} from "../SuggestKaiPlus";
import {ChatViewContext} from "./ChatView";

interface Props {
    message: MessageWithMetadata;
    isStreaming?: boolean;
    onAnimationStart: (position: number) => void;
    onAnimationFinish: (position: number) => void;
    isLastMessageInThread?: boolean;
    heightsMeasured?: boolean; // Flag indicating if parent has measured heights
    deactivated?: boolean;
    isFeedback?: boolean;
}

const AssistantMessage: React.FC<Props> = ({
    message,
    isStreaming,
    onAnimationStart,
    onAnimationFinish,
    isLastMessageInThread,
    heightsMeasured,
    deactivated,
    isFeedback = false,
}) => {
    const {regenerate, resend} = useContext(ChatViewContext);
    const {t} = useTranslation();
    const [copied, setCopied] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const chosenResponseType = useAppSelector(
        (state) => state.session.chosenResponseType
    );
    const transient = useAppSelector((state) => state.transientChat);
    const isLastMessage = useAppSelector((state) => {
        return state.chat.messages?.length - 1 === message.position;
    });
    const threadId = useAppSelector((state) => state.chat.threadId);
    const associatedUserMessage = useAppSelector((state) => {
        const thisMsg = state.chat.messages?.findIndex(
            (m: MessageWithMetadata) => m.position === message.position
        );
        return thisMsg !== undefined
            ? state.chat.messages?.[thisMsg - 1]
            : undefined;
    });

    const handleCopy = () => {
        if (!answer) {
            return;
        }

        const sourcesToShow = message.sources?.filter((source) =>
            answer?.citations?.includes(source.index)
        );

        const htmlFragment = prettyMessageForClipboard(
            answer,
            sourcesToShow,
            message.relatedTools
        );

        const plainText = plainTextForClipboard(
            answer,
            sourcesToShow,
            message.relatedTools
        );

        try {
            const blobHtml = new Blob([htmlFragment], {type: "text/html"});
            const blobText = new Blob([plainText], {type: "text/plain"});

            const clipboardItem = new ClipboardItem({
                "text/html": blobHtml,
                "text/plain": blobText,
            });

            navigator.clipboard.write([clipboardItem]).then(() => {});
        } catch (err) {
            console.error("Failed to copy content:", err);
        }

        setCopied(true);
    };

    const handleResend = useCallback(() => {
        if (associatedUserMessage?.content && associatedUserMessage.position) {
            resend(
                associatedUserMessage.content,
                associatedUserMessage.position
            );
        }
    }, [associatedUserMessage, resend]);

    const handleRegenerate = useCallback(
        (responseType?: KAIResponseType) => {
            regenerate(message.position, responseType);
            previouslyParsedAnswer.current = null;
        },
        [regenerate, message.position]
    );

    const handleClearChat = useCallback(() => {
        dispatch(clearThread());
        navigate("/");
    }, [navigate, dispatch]);

    // This is to prevent the answer flashing when the parsing borks
    // Careful logic here to handle the cases of resending a previous user message
    // and the case of a new message that doesn't have any content because of parsing errors
    const previouslyParsedAnswer = useRef<AssistantAnswer | null>(null);
    const answer = useMemo(() => {
        const json = llmBadJsonToGoodJson(message.content);
        if (
            !json?.content &&
            previouslyParsedAnswer.current?.content &&
            (message.content?.length ?? 0) > 0
        ) {
            return previouslyParsedAnswer.current;
        }

        previouslyParsedAnswer.current = json;
        return json;
    }, [
        associatedUserMessage,
        message.content,
        transient.rateLimitCount,
        isStreaming,
        transient.error,
    ]);

    // Calculate minHeight style using CSS variables
    const minHeightStyle = useMemo(() => {
        // Apply only to the last message in the thread *after* heights are measured
        if (isLastMessageInThread && heightsMeasured) {
            // Subtract user message height and chat input height from viewport height
            // Use fallback values in var()
            return {
                minHeight: `calc(100dvh - var(--last-user-message-height-px, 0px) - var(--chat-input-height-px, 0px) - 1rem)`,
            };
        }
        return {};
    }, [isLastMessageInThread, heightsMeasured]);

    // Effect to signal animation start
    useEffect(() => {
        if (isStreaming) {
            onAnimationStart(message.position);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStreaming, message.position]);

    const showThinkingGradient =
        isStreaming &&
        message.responseType === KAIResponseType.Fast &&
        !answer?.content &&
        !message.activities?.length &&
        message.activities?.length === 0;

    return (
        <div
            style={message.position > 1 ? minHeightStyle : {}}
            className={classes.root}
            data-name="assistant-message"
            onMouseLeave={() => setTimeout(() => setCopied(false), 100)} // Delay because of transition
        >
            <Group
                wrap="nowrap"
                align="flex-start"
                gap={18}
                className={classes.group}
            >
                <IconKAI width={24} height={24} className={classes.icon} />
                <div className={classes.container}>
                    <div className={classes.content}>
                        {showThinkingGradient && (
                            <Text className={classes.thinkingGradient}>
                                {t("common.thinking")}
                            </Text>
                        )}
                        <AssistantWorkBox message={message} />
                        {answer && (
                            <GPTStreamParser
                                key={`${threadId}-${message.position}`}
                                text={answer?.content ?? ""}
                                meta={message}
                                delta={10}
                                options={{
                                    chunkSize: 3,
                                    includeCustomComponents: {
                                        code: true,
                                    },
                                }}
                                onFinish={() =>
                                    onAnimationFinish(message.position)
                                }
                            />
                        )}{" "}
                    </div>
                    {answer?.citations && answer.citations.length > 0 && (
                        <ReferencesBlock
                            id={message.position}
                            items={
                                message.sources?.filter((source) =>
                                    answer?.citations?.includes(source.index)
                                ) ?? []
                            }
                            consideredSources={message.consideredSources ?? []}
                            relatedTools={message.relatedTools ?? []}
                            messageContent={answer?.content}
                            isFeedback={isFeedback}
                        />
                    )}
                    {message.position && !isStreaming && !deactivated && (
                        <Group justify="space-between">
                            <Group gap="sm">
                                {!deactivated && (
                                    <Rater
                                        key={`${threadId}-${message.position}`}
                                        threadId={threadId}
                                        message={message}
                                    />
                                )}
                                <Tooltip
                                    label={
                                        copied
                                            ? t("common.copied")
                                            : t("common.copy")
                                    }
                                >
                                    <Button
                                        variant="message-button"
                                        size="xs"
                                        color={"var(--dib-dark-green)"}
                                        onClick={handleCopy}
                                        disabled={
                                            false
                                            /* message.stopReason ===
                                            ChatStopReason.ContentFilter */
                                        }
                                    >
                                        {copied ? (
                                            <IconCheck height="18px" />
                                        ) : (
                                            <IconCopy height="18px" />
                                        )}
                                    </Button>
                                </Tooltip>
                            </Group>
                            {isLastMessage && (
                                <Tooltip label={t("common.regenerate")}>
                                    <Button
                                        variant="message-button"
                                        size="xs"
                                        color={"var(--dib-dark-green)"}
                                        // TODO: using the chosen response type. switch to drop down
                                        onClick={() =>
                                            handleRegenerate(
                                                message.responseType ??
                                                    chosenResponseType
                                            )
                                        }
                                        disabled={
                                            isStreaming /* ||
                                        message.stopReason ===
                                            ChatStopReason.ContentFilter */
                                        }
                                    >
                                        <IconRefresh height="18px" />
                                    </Button>
                                </Tooltip>
                            )}
                        </Group>
                    )}
                    {/* <FollowUpQuestions
                        mounted={finished}
                        questions={message.followUpQuestions ?? []}
                        onSubmit={onFollowUpQuestionSubmit}
                    />
                    <StopReason
                        value={message.stopReason}
                        onResend={handleResend}
                        onExit={handleClearChat}
                    /> */}
                    {transient.threadId === threadId && (
                        <ErrorReason
                            threadId={threadId}
                            value={transient.error}
                            onResend={handleResend}
                            onExit={handleClearChat}
                        />
                    )}
                    <SuggestKaiPlus
                        onRegenerate={handleRegenerate}
                        message={message}
                    />
                </div>
            </Group>
        </div>
    );
};

export default AssistantMessage;
